import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { debounce } from 'lodash';
import SourceLinkedText from './SourceLinkedText';
import { renumberInTextCitations } from '../../utils/citationUtils';
import {
  Box,
  Grid,
  GridItem,
  HStack,
  VStack,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useBreakpointValue,
  Heading,
} from '@chakra-ui/react';
import {
  Clock,
  MapPin,
} from '@phosphor-icons/react';

const extractValue = (fieldData) => {
  if (typeof fieldData === 'object' && fieldData !== null && fieldData.hasOwnProperty('value')) {
    return fieldData.value || '';
  }
  if (typeof fieldData === 'string') {
    return fieldData;
  }
  return '';
};

export default function SiteBasicInfo({
  site,
  editedSite,
  isEditing,
  onEditChange,
  showCitations,
  onToggleShowCitations,
}) {
  const displayData = site || {};
  const editData = editedSite || {};

  const iconSize = useBreakpointValue({ base: 18, md: 22 });
  const iconWeight = useBreakpointValue({ base: "regular", md: "light" });

  const sectionsConfig = [
    { field: 'overviewIntroduction', title: 'Overview / Introduction' },
    { field: 'discoveryAndExcavation', title: 'Discovery and Excavation' },
    { field: 'historicalContextAndChronology_narrative', title: 'Historical Context & Chronology' },
    { field: 'keyArchitecturalFeaturesLayout', title: 'Key Architectural Features / Layout' },
    { field: 'purposeAndFunction', title: 'Purpose and Function' },
    { field: 'culturalSignificance_narrative', title: 'Cultural Significance (Narrative)' },
    { field: 'notableFindsArtifacts_narrative', title: 'Notable Finds / Artifacts (Narrative)' },
    { field: 'theoriesAndInterpretations', title: 'Theories and Interpretations' },
    { field: 'currentStatusAndPreservation_narrative', title: 'Current Status and Preservation (Narrative)' },
  ];

  const handleChange = (field, value) => {
    onEditChange(field, value);
  };

  if (isEditing) {
    return (
      <VStack align="stretch" spacing={4} width="100%">
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
          <FormControl>
            <FormLabel fontSize="sm" color="text.secondary">Site Name</FormLabel>
            <Input
              variant="journal"
              value={editData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="text.secondary">Culture</FormLabel>
            <Input
              variant="journal"
              value={editData.culture || ''}
              onChange={(e) => handleChange('culture', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="text.secondary">Country</FormLabel>
            <Input
              variant="journal"
              value={editData.country || ''}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="text.secondary">City/Region</FormLabel>
            <Input
              variant="journal"
              value={editData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </FormControl>
        </Grid>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={{ base: 6, md: 8 }}>
      {/* Quick Stats Cards */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
        {/* Era Card */}
        <Box
          bg="whiteAlpha.400"
          borderRadius="xl"
          p={4}
          border="1px solid"
          borderColor="whiteAlpha.300"
          backdropFilter="blur(5px)"
          _hover={{
            bg: "whiteAlpha.500",
            transform: "translateY(-2px)",
            transition: "all 0.2s ease"
          }}
          transition="all 0.2s ease"
        >
          <HStack spacing={3} align="center">
            <Box 
              color="interactive.primary" 
              bg="whiteAlpha.200"
              borderRadius="full" 
              p={3}
              boxShadow="md"
            >
              <Clock size={iconSize} weight="duotone" />
            </Box>
            <VStack align="start" spacing={1} flex={1}>
              <Text 
                fontSize="xs" 
                color="text.muted" 
                fontFamily="heading" 
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="semibold"
              >
                Historical Era
              </Text>
              <Text 
                fontFamily="heading" 
                fontWeight="bold" 
                fontSize={{ base: "md", md: "lg" }} 
                color="text.default"
                lineHeight="shorter"
              >
                {(() => {
                  const mainPeriod = displayData.constructionPeriods?.[displayData.mainPeriodIndex ?? 0];
                  if (!mainPeriod) return 'Not specified';
                  return mainPeriod.startEra && mainPeriod.endEra ? `${mainPeriod.startEra} - ${mainPeriod.endEra}` : mainPeriod.startEra || mainPeriod.endEra || 'Unknown';
                })()}
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* Location Card */}
        <Box
          bg="whiteAlpha.400"
          borderRadius="xl"
          p={4}
          border="1px solid"
          borderColor="whiteAlpha.300"
          backdropFilter="blur(5px)"
          _hover={{
            bg: "whiteAlpha.500",
            transform: "translateY(-2px)",
            transition: "all 0.2s ease"
          }}
          transition="all 0.2s ease"
        >
          <HStack spacing={3} align="center">
            <Box 
              color="interactive.primary" 
              bg="whiteAlpha.200"
              borderRadius="full" 
              p={3}
              boxShadow="md"
            >
              <MapPin size={iconSize} weight="duotone" />
            </Box>
            <VStack align="start" spacing={1} flex={1}>
              <Text 
                fontSize="xs" 
                color="text.muted" 
                fontFamily="heading" 
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="semibold"
              >
                Location
              </Text>
              <Text 
                fontFamily="body" 
                fontWeight="medium" 
                fontSize={{ base: "md", md: "lg" }}
                color="text.default"
                lineHeight="shorter"
              >
                {(() => {
                  if (displayData.location?.region && displayData.location?.country) {
                    return `${displayData.location.region}, ${displayData.location.country}`;
                  }
                  return displayData.location?.country || displayData.location?.region || displayData.location?.address || 'Location not specified';
                })()}
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Grid>

      {/* Culture Section */}
      {(() => {
        const mainPeriod = displayData.constructionPeriods?.[displayData.mainPeriodIndex ?? 0];
        const culture = mainPeriod?.culture;
        if (!culture) return null;
        return (
          <Box
            bg="whiteAlpha.400"
            borderRadius="xl"
            p={5}
            border="1px solid"
            borderColor="whiteAlpha.300"
            backdropFilter="blur(5px)"
            _hover={{
              bg: "whiteAlpha.500",
              transform: "translateY(-2px)",
              transition: "all 0.2s ease"
            }}
            transition="all 0.2s ease"
          >
            <HStack spacing={4} align="center">
              <Box 
                color="interactive.primary" 
                bg="whiteAlpha.200"
                borderRadius="full" 
                p={3}
                boxShadow="md"
              >
                <Text fontSize="1.5rem" fontWeight="bold" lineHeight="1">&Omega;</Text>
              </Box>
              <VStack align="start" spacing={1} flex={1}>
                <Text 
                  fontSize="xs" 
                  color="text.muted" 
                  fontFamily="heading" 
                  textTransform="uppercase"
                  letterSpacing="wider"
                  fontWeight="semibold"
                >
                  Cultural Heritage
                </Text>
                <Text 
                  fontFamily="heading" 
                  fontWeight="bold" 
                  fontSize={{ base: "lg", md: "xl" }}
                  color="text.default"
                  lineHeight="shorter"
                >
                  {culture}
                </Text>
              </VStack>
            </HStack>
          </Box>
        );
      })()}

      {/* Description/Overview */}
      {(() => {
        const overview = extractValue(displayData.overviewIntroduction);
        if (!overview) return null;
        
        // Get first 2-3 sentences for preview
        const sentences = overview.match(/[^.!?]+[.!?]+/g) || [];
        const preview = sentences.slice(0, 2).join(' ').trim();
        
        if (!preview) return null;
        
        return (
          <Box
            bg="whiteAlpha.300"
            borderRadius="xl"
            p={6}
            border="1px solid"
            borderColor="whiteAlpha.200"
            backdropFilter="blur(5px)"
          >
            <VStack align="start" spacing={3}>
              <Text 
                fontSize="sm" 
                color="text.muted" 
                fontFamily="heading" 
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="semibold"
              >
                Site Overview
              </Text>
              <Text 
                fontFamily="body" 
                fontSize={{ base: "md", md: "lg" }}
                color="text.default"
                lineHeight="tall"
                fontStyle="italic"
              >
                "{preview}"
              </Text>
            </VStack>
          </Box>
        );
      })()}
    </VStack>
  );
}
